import '../scrollytelling.css'
import '../v2/cinematic.css'
import './usecases.css'

import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useCallback, useMemo, useState } from 'react'

import { SceneProvider, useScrollStory } from '../engine'
import { LANGS, StoryProvider, readLang, writeParam } from './i18n'

/**
 * Rahmen der Use-Case-Stories im Stil von Version 2 „Cinematic“.
 * `sections` wie in Version 2 (id, height in vh, tone, Scene). Mit `flow`
 * steht eine Section nicht still, sondern scrollt normal (z.B. FAQ).
 * `copy` ist `{ de, en }`, die Kapitelnamen stehen in `copy[lang].chapters`.
 */
export default function CinematicShell({ sections, copy, className }) {
  const [lang, setLang] = useState(readLang)

  const choose = (next) => {
    if (next === lang) return
    writeParam('lang', next === 'de' ? null : next)
    setLang(next)
  }

  // Neu aufbauen beim Sprachwechsel, die Engine misst danach sofort wieder
  return (
    <Story
      key={lang}
      sections={sections}
      copy={copy[lang]}
      lang={lang}
      onLang={choose}
      className={className}
    />
  )
}

CinematicShell.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      height: PropTypes.number,
      tone: PropTypes.string.isRequired,
      flow: PropTypes.bool,
      Scene: PropTypes.elementType.isRequired
    })
  ).isRequired,
  copy: PropTypes.shape({ de: PropTypes.object, en: PropTypes.object })
    .isRequired,
  className: PropTypes.string
}

function Story({ sections, copy, lang, onLang, className }) {
  const story = useScrollStory(sections.length, { mode: 'pin' })
  const current = sections[story.active]
  const { scrollToChapter } = story

  const goTo = useCallback(
    (id) => {
      const index = sections.findIndex((s) => s.id === id)
      if (index >= 0) scrollToChapter(index)
    },
    [sections, scrollToChapter]
  )

  const value = useMemo(() => ({ lang, copy, goTo }), [lang, copy, goTo])

  return (
    <StoryProvider value={value}>
      <div
        ref={story.storyRef}
        lang={lang}
        className={clsx('sty-root cin-root uc-root', className)}
      >
        <div
          ref={story.progressRef}
          className={clsx('cin-bar', `cin-bar--${current.tone}`)}
        >
          <div className='cin-bar__fill' />
          <nav className='cin-bar__nav uc-nav' aria-label={copy.ui.chapters}>
            {sections.map((s, i) => (
              <button
                key={s.id}
                type='button'
                className={clsx(
                  'cin-bar__dot',
                  i === story.active && 'is-active'
                )}
                onClick={() => story.scrollToChapter(i)}
                aria-label={copy.chapters[s.id]}
                title={copy.chapters[s.id]}
              />
            ))}
            <span className='cin-bar__label'>{copy.chapters[current.id]}</span>
            <span className='uc-lang' role='group' aria-label='Sprache'>
              {LANGS.map((l) => (
                <button
                  key={l}
                  type='button'
                  className={clsx('uc-lang__btn', l === lang && 'is-active')}
                  aria-pressed={l === lang}
                  onClick={() => onLang(l)}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </span>
          </nav>
        </div>

        {sections.map(({ id, height, tone, flow, Scene }, i) => (
          <section
            key={id}
            id={`uc-${id}`}
            ref={(el) => (story.chapterRefs.current[i] = el)}
            className={clsx(
              'cin-section',
              `cin-tone--${tone}`,
              flow && 'uc-flow'
            )}
            style={flow ? undefined : { height: `${height}vh` }}
            aria-label={copy.chapters[id]}
          >
            <div
              ref={(el) => (story.sceneRefs.current[i] = el)}
              className={flow ? 'uc-flow__inner' : 'cin-sticky'}
            >
              <SceneProvider index={i} subscribe={story.subscribe}>
                <Scene />
              </SceneProvider>
            </div>
          </section>
        ))}
      </div>
    </StoryProvider>
  )
}

Story.propTypes = {
  sections: CinematicShell.propTypes.sections,
  copy: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  onLang: PropTypes.func.isRequired,
  className: PropTypes.string
}
